(() => {
  if (customElements.get('routine-guide')) return;

  class RoutineGuide extends HTMLElement {
    connectedCallback() {
      this.addButtons = [...this.querySelectorAll('[data-routine-add]')];
      this.navLinks = [...this.querySelectorAll('[data-routine-nav]')];
      this.videoCovers = [...this.querySelectorAll('[data-routine-video-cover]')];
      this.onAdd = this.onAdd.bind(this);
      this.onVideoCover = this.onVideoCover.bind(this);

      this.addButtons.forEach((button) => button.addEventListener('click', this.onAdd));
      this.videoCovers.forEach((cover) => cover.addEventListener('click', this.onVideoCover));
      this.observeChapters();
    }

    disconnectedCallback() {
      this.addButtons?.forEach((button) => button.removeEventListener('click', this.onAdd));
      this.videoCovers?.forEach((cover) => cover.removeEventListener('click', this.onVideoCover));
      this.chapterObserver?.disconnect();
    }

    onVideoCover(event) {
      const cover = event.currentTarget;
      const video = cover.closest('[data-routine-video]')?.querySelector('video');
      if (!video) return;

      cover.hidden = true;
      const playRequest = video.play();
      if (playRequest?.catch) {
        playRequest.catch(() => {
          cover.hidden = false;
        });
      }
    }

    observeChapters() {
      if (!('IntersectionObserver' in window) || !this.navLinks.length) return;

      this.chapterObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const link = this.querySelector(`[data-routine-nav][href="#${CSS.escape(entry.target.id)}"]`);
            this.navLinks.forEach((navLink) => navLink.removeAttribute('aria-current'));
            link?.setAttribute('aria-current', 'true');
          });
        },
        { rootMargin: '-25% 0px -65%', threshold: 0 }
      );

      this.querySelectorAll('[data-routine-chapter]').forEach((chapter) => {
        this.chapterObserver.observe(chapter);
      });
    }

    async onAdd(event) {
      const button = event.currentTarget;
      if (button.disabled || button.classList.contains('is-loading')) return;

      const variantIds = (button.dataset.variantIds || '')
        .split(',')
        .map((id) => Number(id.trim()))
        .filter(Boolean);
      const status = button.parentElement.querySelector('[data-routine-status]');
      const label = button.querySelector('[data-routine-add-label]');
      const defaultLabel = button.dataset.defaultLabel || 'Shop the routine';

      if (!variantIds.length) {
        if (status) status.textContent = 'This routine is currently unavailable.';
        return;
      }

      button.classList.add('is-loading');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (status) status.textContent = 'Adding the routine to your cart…';

      try {
        const response = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: variantIds.map((id) => ({ id, quantity: 1 }))
          })
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this routine.');

        label.textContent = 'Routine added';
        if (status) status.textContent = 'Your complete Puffia routine has been added to the cart.';
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));

        window.setTimeout(() => {
          label.textContent = defaultLabel;
        }, 3000);
      } catch (error) {
        label.textContent = defaultLabel;
        if (status) status.textContent = error.message || 'Something went wrong. Please try again.';
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('routine-guide', RoutineGuide);
})();
